import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, InputGroup, Badge, Button, Modal } from 'react-bootstrap';
import { getUsers, updateUserRole, deleteUser } from '../../actions/users';
import '../../interfaceSettings.css';

const ProfileControlPanel = () => {
    const dispatch = useDispatch();
    const users = useSelector((state) => state.users.users);

    useEffect(() => {
        dispatch(getUsers());
    }, [dispatch]);

    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const filteredProfiles = users.filter((profile) =>
        profile.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleDeleteButton = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    const confirmDeleteUser = async () => {
        try {
            dispatch(deleteUser(selectedUser._id));
            setShowDeleteModal(false);
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const handleAdminUpdateButton = (user) => {
        setSelectedUser(user);
        setShowRoleModal(true);
    };

    const confirmRoleChange = async () => {
        try {
            const newRole = selectedUser.roleType === 'User' ? 'Admin' : 'User';
            dispatch(updateUserRole(selectedUser._id, { roleType: newRole }));
            setShowRoleModal(false);
        } catch (error) {
            console.error('Error updating user role:', error);
        }
    };

    return (
        <div className='p-4'>
            <h1 id="role-manager-title"> Role Manager</h1>
            <div className="container col-lg-12 col-md-10 p-3 pb-3 text-center card shadow-sm">
                <div className="col-lg-12" style={{ display: 'flex', alignItems: 'center' }}>
                    <InputGroup>
                        <InputGroup.Text id="bg-body-tertiary justify-content-between basic-addon1">🔎</InputGroup.Text>
                        <Form.Control
                                type="text"
                                className="input-field"
                                placeholder="Search Profiles"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                required
                                style={{ marginRight: '8px' }}
                                aria-label="Search Profiles"
                        />
                    </InputGroup>
                </div>
                <hr />
                {/* Desktop/Tablet: Table view */}
                <div className='table-responsive d-none d-md-block'>
                    <table className="table table-hover w-100">
                        <thead>
                            <tr>
                                <th className="align-middle">Username</th>
                                <th className="align-middle d-none d-md-table-cell">Email</th>
                                <th className="align-middle" style={{ width: '200px' }}>Role</th>
                                <th className="align-middle" style={{ width: '100px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/*Displays Profile in Row */}
                            {
                                filteredProfiles.length > 0 ? (
                                    filteredProfiles.map(profile => (
                                        <tr key={profile._id}>
                                            <td className="align-middle">{profile.username}</td>
                                            <td className="align-middle d-none d-md-table-cell">{profile.email}</td>
                                            <td className="align-middle">
                                                <div className="d-flex flex-column align-items-center">
                                                    <Badge
                                                        bg={profile.roleType === 'Admin' ? 'primary' : 'secondary'}
                                                        className="mb-2"
                                                        style={{ fontSize: '1rem', padding: '8px 12px' }}
                                                    >
                                                        {profile.roleType}
                                                    </Badge>
                                                    <Button
                                                        variant={profile.roleType === 'Admin' ? 'outline-primary' : 'outline-secondary'}
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        style={{ whiteSpace: 'nowrap' }}
                                                        onClick={() => handleAdminUpdateButton(profile)}
                                                    >
                                                        <i className="fas fa-exchange-alt me-1"></i>
                                                        Change Role
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="align-middle">
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="w-100"
                                                    onClick={() => handleDeleteButton(profile)}
                                                >
                                                    <i className="fas fa-trash-alt me-1"></i>
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center', padding: '8px' }}>
                                            No results found
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>

                {/* Mobile: Card view */}
                <div className='d-md-none'>
                    {filteredProfiles.length > 0 ? (
                        filteredProfiles.map(profile => (
                            <div key={profile._id} className="card mb-3 shadow-sm">
                                <div className="card-body">
                                    <h2 className="card-title mb-2 h5">{profile.username}</h2>
                                    <p className="card-text text-muted mb-3" style={{ fontSize: '0.95rem' }}>{profile.email}</p>
                                    
                                    <div className="d-flex align-items-center justify-content-center gap-2 mb-3 pb-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                                        <span className="fw-bold" style={{ fontSize: '20px' }}>Role:</span>
                                        <Badge 
                                            bg={profile.roleType === 'Admin' ? 'primary' : 'secondary'}
                                            style={{ fontSize: '20px', padding: '12px 20px', lineHeight: '1.5' }}
                                        >
                                            {profile.roleType}
                                        </Badge>
                                    </div>
                                    
                                    <div className="d-grid gap-2">
                                        <Button
                                            variant={profile.roleType === 'Admin' ? 'outline-primary' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => handleAdminUpdateButton(profile)}
                                        >
                                            <i className="fas fa-exchange-alt me-2"></i>
                                            Change Role
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleDeleteButton(profile)}
                                        >
                                            <i className="fas fa-trash-alt me-2"></i>
                                            Delete User
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center p-4 text-muted">
                            No results found
                        </div>
                    )}
                </div>
            </div>
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete <strong>{selectedUser?.username}</strong>?
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                    <Button className="btn-cancel modal-action-btn" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" className="modal-action-btn" onClick={confirmDeleteUser}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Change Role Confirmation Modal */}
            <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Role Change</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to change <strong>{selectedUser?.username}</strong>'s role from <strong>{selectedUser?.roleType}</strong> to <strong>{selectedUser?.roleType === 'User' ? 'Admin' : 'User'}</strong>?
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                    <Button variant="danger" className="modal-action-btn" onClick={() => setShowRoleModal(false)} >
                        Cancel
                    </Button>
                    <Button variant="primary" className="modal-action-btn" onClick={confirmRoleChange}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default ProfileControlPanel;
